import axios from 'axios';
import urlJoin from 'url-join';
import { getCurrentBackendEnvironment } from 'helpers';

/**
 * Builds the headers we need for a call
 * @param {string} sessionToken
 */
const buildHeaders = (sessionToken) => {
  const headers = {
    'X-Around-Api-Token': getCurrentBackendEnvironment().apiToken
  };
  if (sessionToken) {
    headers['X-Around-Session-Token'] = sessionToken;
  }
  return headers;
};

const buildMondayHeaders = () => {
  const headers = {
    Authorization: getCurrentBackendEnvironment().mondayAPIToken
  };
  return headers;
};

/**
 * Login into the Prompto API
 * @param {String} usernameOrEmail - Username or email
 * @param {String} password - Password
 */
export const login = (usernameOrEmail, password) => {
  if (!usernameOrEmail) {
    return Promise.reject(new Error('no username or email is given'));
  }

  if (!password) {
    return Promise.reject(new Error('no password is given'));
  }

  const body = {
    usernameOrEmail,
    password,
    entityObjectId: 'aroundmedia'
  };

  return axios.post(
    urlJoin(
      getCurrentBackendEnvironment().baseUrl,
      'users/login',
      `?_=${Date.now()}`
    ),
    body,
    {
      headers: buildHeaders()
    }
  );
};

/**
 * Return all the vaults
 * https://devapiv2.vr-tual.media/asciidoc/index.html#_getall_16
 * @param {string} sessionToken Session token of the user that is logged in
 */
export const getAllVaults = (sessionToken) => {
  return axios.get(
    urlJoin(
      getCurrentBackendEnvironment().baseUrl,
      'entities/aroundmedia/vaults',
      `?_=${Date.now()}`
    ),
    {
      params: {
        includeVaultPurchaseInfo: true
      },
      headers: buildHeaders(sessionToken)
    }
  );
};

export const getAllVaultsFiltered = (sessionToken) => {
  return axios.get(
    urlJoin(
      getCurrentBackendEnvironment().baseUrl,
      'entities/aroundmedia/vaults/filterByPlans',
      `?planIds=prompto-tiered-yearly-premium-euro&planIds=prompto-tiered-yearly-business-euro&planIds=prompto-tiered-yearly-essential-euro&planIds=prompto-tiered-monthly-premium-euro&planIds=prompto-tiered-monthly-business-euro&planIds=prompto-tiered-monthly-essential-euro&_=${Date.now()}`
    ),
    {
      headers: buildHeaders(sessionToken)
    }
  );
};

/**
 * Get all the user that can access the given vault
 * @param {string} sessionToken
 */
export const getAllUsersThatCanAccessVault = (vaultId, sessionToken) => {
  return axios.get(
    urlJoin(
      getCurrentBackendEnvironment().baseUrl,
      'vaults',
      vaultId,
      'users',
      `?_=${Date.now()}`
    ),
    {
      headers: buildHeaders(sessionToken)
    }
  );
};

/**
 * Get all the projects for the given vault
 * https://devapiv2.vr-tual.media/asciidoc/index.html#_getall_14
 * @param {string} vaultObjectId The object id of the current vault
 * @param {string} sessionToken
 */
export const getAllProjects = (vaultObjectId, sessionToken) => {
  return axios.get(
    urlJoin(
      getCurrentBackendEnvironment().baseUrl,
      'vaults',
      vaultObjectId,
      'projectSections',
      `?_=${Date.now()}`
    ),
    {
      params: {
        includeUnitList: true
      },
      headers: buildHeaders(sessionToken)
    }
  );
};

export const getProjectResources = (vaultObjectId, projectObjectId, amountOfImages = 1) => {
  return axios.get(
    urlJoin(
      getCurrentBackendEnvironment().baseUrl,
      'marketplace/prompto/getProjectResources/vault',
      vaultObjectId,
      'project',
      projectObjectId,
      `?_=${Date.now()}`
    ),
    {
      params: {
        amountOfImages
      },
      headers: buildHeaders()
    }
  );
};

/**
 * Get Navigation Collection
 * @param {string} navigationCollectionObjectId
 * @param {string} sessionToken
 */
export const getNavigationCollection = (
  navigationCollectionObjectId,
  sessionToken
) => {
  const env = getCurrentBackendEnvironment();

  return axios.get(
    urlJoin(
      env.baseUrl,
      'navigationcollections',
      navigationCollectionObjectId,
      `?_=${Date.now()}`
    ),
    {
      headers: buildHeaders(sessionToken)
    }
  );
};

export const getContentCollection = (contentCollectionObjectId) => {
  return axios.get(
    urlJoin(
      getCurrentBackendEnvironment().baseUrl,
      'contentcollections',
      contentCollectionObjectId,
      `?_=${Date.now()}`
    ),
    {
      headers: buildHeaders()
    }
  );
};

/**
 * Get a token to access the google storage
 * https://devapiv2.vr-tual.media/asciidoc/index.html#_getstorageaccesstoken
 * @param {string} vaultId
 * @param {string} sessionToken
 * @returns
 */
export const vaultGetUploadAccessToken = (sessionToken) => {
  return axios.get(
    urlJoin(
      getCurrentBackendEnvironment().baseUrl,
      'vaults',
      getCurrentBackendEnvironment().promptoVault,
      '/catalogue/storage/token',
      `?_=${Date.now()}`
    ),
    {
      headers: buildHeaders(sessionToken)
    }
  );
};

export const getAllMarketplaceShortUrls = (sessionToken) => {
  return axios.get(
    urlJoin(
      getCurrentBackendEnvironment().baseUrl,
      'shareitems/marketplaceProject/getAllRedirections',
      `?_=${Date.now()}`
    ),
    {
      headers: buildHeaders(sessionToken)
    }
  );
};

export const archiveMarketplaceProject = (
  marketplaceId,
  projectId,
  vaultId,
  sessionToken
) => {
  return axios.put(
    urlJoin(
      getCurrentBackendEnvironment().baseUrl,
      `marketplace/${marketplaceId}/archiveProject/vault/${vaultId}/project/${projectId}`,
      `?_=${Date.now()}`
    ),
    {},
    {
      headers: buildHeaders(sessionToken)
    }
  );
};

export const publishMarketplaceProject = (
  marketplaceId,
  projectId,
  vaultId,
  sessionToken
) => {
  return axios.post(
    urlJoin(
      getCurrentBackendEnvironment().baseUrl,
      `marketplace/${marketplaceId}/publishProject`,
      `?_=${Date.now()}`
    ),
    {
      vaultObjectId: vaultId,
      projectObjectId: projectId
    },
    {
      headers: buildHeaders(sessionToken)
    }
  );
};

export const updateMarketplaceProjectTargetLeads = (
  marketplaceProjectId,
  targetLeads,
  sessionToken
) => {
  return axios.put(
    urlJoin(
      getCurrentBackendEnvironment().baseUrl,
      `marketplaceProject/${marketplaceProjectId}`
    ),
    {
      targetLeads: targetLeads
    },
    {
      headers: buildHeaders(sessionToken)
    }
  );
};

export const createMarketplaceShortUrl = (
  marketplaceId,
  projectId,
  vaultId,
  shareCode,
  sessionToken
) => {
  return axios.post(
    urlJoin(
      getCurrentBackendEnvironment().baseUrl,
      `shareitems/marketplaceProject/createShortUrl`,
      `?_=${Date.now()}`
    ),
    {
      shareCode: shareCode,
      vaultObjectId: vaultId,
      projectObjectId: projectId,
      marketplaceId: marketplaceId
    },
    {
      headers: buildHeaders(sessionToken)
    }
  );
};

export const getJsonFile = (fileUrl) => {
  return axios.get(fileUrl);
};

export const getMondayBoardItemFromProjectId = (projectId) => {
  const query = `{items_page_by_column_values (limit: 50, board_id: ${
    getCurrentBackendEnvironment().mondayBoardId
  }, columns: [{column_id: "${
    getCurrentBackendEnvironment().mondayProjectColumnId
  }", column_values: ["${projectId}"]}]) { cursor items { id name } } }`;

  return axios.post(
    urlJoin(getCurrentBackendEnvironment().mondayBaseUrl),
    {
      query
    },
    {
      headers: buildMondayHeaders()
    }
  );
};

export const getAllMondayProjects = () => {
  const query = `{ boards (ids: ${
    getCurrentBackendEnvironment().mondayBoardId
  }){ items_page (limit: 500) { items {id name column_values { id  text value } parent_item { id name column_values { id text value} } } } } }`;
  return axios.post(
    urlJoin(getCurrentBackendEnvironment().mondayBaseUrl),
    {
      query
    },
    {
      headers: buildMondayHeaders()
    }
  );
};

export const updateLeadAmountForProject = (itemId, leadAmount) => {
  const query = `mutation {change_column_value(item_id: ${itemId}, board_id: ${
    getCurrentBackendEnvironment().mondayBoardId
  }, column_id: "${
    getCurrentBackendEnvironment().mondayLeadsThisMonthColumnId
  }", value: "${leadAmount}") {id}}`;

  return axios.post(
    urlJoin(getCurrentBackendEnvironment().mondayBaseUrl),
    {
      query
    },
    {
      headers: buildMondayHeaders()
    }
  );
};

export const getAllUnpaidInvoices = (sessionToken) => {
  return axios.post(
    urlJoin(
      getCurrentBackendEnvironment().baseUrl,
      `/admin/chargebee/invoices`,
      `?_=${Date.now()}`
    ),
    {
      statusList: ['PAYMENT_DUE', 'NOT_PAID']
    },
    {
      headers: buildHeaders(sessionToken)
    }
  );
};

export const authenticateYuki = (sessionToken, accessKey) => {
  return axios.post(
    urlJoin(
      getCurrentBackendEnvironment().baseUrl,
      `/admin/yuki/authenticate`,
      `?_=${Date.now()}`
    ),
    { accessKey },
    {
      headers: buildHeaders(sessionToken)
    }
  );
};

export const getYukiPayments = (
  sessionToken,
  sessionID,
  administrationID,
  startDate,
  endDate
) => {
  return axios.post(
    urlJoin(
      getCurrentBackendEnvironment().baseUrl,
      `/admin/yuki/payments`,
      `?_=${Date.now()}`
    ),
    { sessionID, administrationID, startDate, endDate },
    {
      headers: buildHeaders(sessionToken)
    }
  );
};

export const recordChargebeePayment = (sessionToken, invoiceId, amount, comment) => {
  return axios.post(
    urlJoin(
      getCurrentBackendEnvironment().baseUrl,
      `/admin/chargebee/recordPayment`,
      `?_=${Date.now()}`
    ),
    { id: invoiceId, amount, comment },
    {
      headers: buildHeaders(sessionToken)
    }
  );
};

export const sendAiMessage = (
  sessionToken,
  content,
  character,
  adminConversationId
) => {
  const body = {
    aiAgent: 'GPT',
    character,
    content
  };
  if (adminConversationId) {
    body.adminConversationId = adminConversationId;
  }

  return axios.post(
    urlJoin(
      getCurrentBackendEnvironment().baseUrl,
      `/ai/sendMessage`,
      `?_=${Date.now()}`
    ),
    body,
    {
      headers: buildHeaders(sessionToken)
    }
  );
};

export const getAllVaultsForAdmin = (managedByVaultId, sessionToken) => {
  return axios.get(
    urlJoin(
      getCurrentBackendEnvironment().baseUrl,
      `vaults/getAllVaultsManagedByVault/${managedByVaultId}`,
      `?_=${Date.now()}`
    ),
    {
      headers: buildHeaders(sessionToken)
    }
  );
};

export const sendUnitSoldMessageToSlack = (companyName, unitName) => {
  const webhookUrl =
    'https://hooks.slack.com/services/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';
  const corsProxyUrl = 'https://corsproxy.io/?';
  const fullUrl = corsProxyUrl + webhookUrl;

  return axios.post(fullUrl, {
    blocks: [
      {
        type: 'divider'
      },
      {
        type: 'rich_text',
        elements: [
          {
            type: 'rich_text_section',
            elements: [
              {
                type: 'text',
                text: `${companyName} `,
                style: {
                  italic: true,
                  bold: true
                }
              },
              {
                type: 'text',
                text: 'has just sold unit '
              },
              {
                type: 'text',
                text: `${unitName}! `,
                style: {
                  italic: true,
                  bold: true
                }
              },
              {
                type: 'emoji',
                name: 'tada'
              }
            ]
          }
        ]
      },
      {
        type: 'section',
        text: {
          type: 'plain_text',
          text: ' '
        }
      },
      {
        type: 'divider'
      }
    ]
  });
};

export const getAllMetaCampaigns = () => {
  return axios.get(
    'https://graph.facebook.com/v21.0/act_599346272029704/campaigns',
    {
      params: {
        fields: 'name, status, stop_time',
        access_token:
          'EAAFkmxt66E8BO7aK8PJZBcO4ZAARsLQrDEgrNuRi6VZAGnpdsQo6aldt4aX3IQCldU9GH4VRp1KW8mX5v4cJdyLowxNCt7rrj5enaQeVrRqZCvcRk7YBotd8qsm4G0BRbqvPatfIjQvEUvhRMdbvZByO1LHRxmBMdePAbWMHJcW2gedyRO9mwQZCZCkcqXYDwZDZD',
        limit: 500
      }
    }
  );
};

export const getAllRunningMetaAdSets = (timestamp) => {
  return axios.get(
    'https://graph.facebook.com/v21.0/act_599346272029704/adsets',
    {
      params: {
        fields: 'id,name,end_time',
        filtering: `[{"field":"end_time","operator":"GREATER_THAN","value":${
          timestamp / 1000
        }}]`,
        access_token:
          'EAAFkmxt66E8BO7aK8PJZBcO4ZAARsLQrDEgrNuRi6VZAGnpdsQo6aldt4aX3IQCldU9GH4VRp1KW8mX5v4cJdyLowxNCt7rrj5enaQeVrRqZCvcRk7YBotd8qsm4G0BRbqvPatfIjQvEUvhRMdbvZByO1LHRxmBMdePAbWMHJcW2gedyRO9mwQZCZCkcqXYDwZDZD',
        limit: 1000
      }
    }
  );
};

export const getAllMetaAds = () => {
  const filtering = [
    { field: 'effective_status', operator: 'IN', value: ['ACTIVE'] }
  ];

  return axios.get('https://graph.facebook.com/v21.0/act_599346272029704/ads', {
    params: {
      fields:
        'name,insights{reach,spend,cost_per_action_type},created_time,adset_id',
      filtering: JSON.stringify(filtering),
      access_token:
        'EAAFkmxt66E8BO7aK8PJZBcO4ZAARsLQrDEgrNuRi6VZAGnpdsQo6aldt4aX3IQCldU9GH4VRp1KW8mX5v4cJdyLowxNCt7rrj5enaQeVrRqZCvcRk7YBotd8qsm4G0BRbqvPatfIjQvEUvhRMdbvZByO1LHRxmBMdePAbWMHJcW2gedyRO9mwQZCZCkcqXYDwZDZD',
      limit: 2000
    }
  });
};

export const getAllInsightsForMetaCampaigns = (selectedDataPreset) => {
  return axios.get(
    'https://graph.facebook.com/v21.0/act_599346272029704/insights',
    {
      params: {
        fields: 'campaign_name,campaign_id,spend,updated_time',
        level: 'campaign',
        date_preset: selectedDataPreset,
        access_token:
          'EAAFkmxt66E8BO7aK8PJZBcO4ZAARsLQrDEgrNuRi6VZAGnpdsQo6aldt4aX3IQCldU9GH4VRp1KW8mX5v4cJdyLowxNCt7rrj5enaQeVrRqZCvcRk7YBotd8qsm4G0BRbqvPatfIjQvEUvhRMdbvZByO1LHRxmBMdePAbWMHJcW2gedyRO9mwQZCZCkcqXYDwZDZD',
        limit: 1000
      }
    }
  );
};

export const getInsightsForMetaCampaign = (campaignId, selectedDataPreset) => {
  return axios.get(`https://graph.facebook.com/v21.0/${campaignId}/insights`, {
    params: {
      date_preset: selectedDataPreset,
      access_token:
        'EAAFkmxt66E8BO7aK8PJZBcO4ZAARsLQrDEgrNuRi6VZAGnpdsQo6aldt4aX3IQCldU9GH4VRp1KW8mX5v4cJdyLowxNCt7rrj5enaQeVrRqZCvcRk7YBotd8qsm4G0BRbqvPatfIjQvEUvhRMdbvZByO1LHRxmBMdePAbWMHJcW2gedyRO9mwQZCZCkcqXYDwZDZD'
    }
  });
};

export const getInsightsForMetaAd = (adId, selectedDataPreset) => {
  return axios.get(`https://graph.facebook.com/v21.0/${adId}/insights`, {
    params: {
      date_preset: selectedDataPreset,
      fields: 'reach,spend,cost_per_action_type',
      action_breakdowns: 'action_type',
      access_token:
        'EAAFkmxt66E8BO7aK8PJZBcO4ZAARsLQrDEgrNuRi6VZAGnpdsQo6aldt4aX3IQCldU9GH4VRp1KW8mX5v4JdyLowxNCt7rrj5enaQeVrRqZCvcRk7YBotd8qsm4G0BRbqvPatfIjQvEUvhRMdbvZByO1LHRxmBMdePAbWMHJcW2gedyRO9mwQZCZCkcqXYDwZDZD'
    }
  });
};

// META ACCESS TOKEN: EAAFkmxt66E8BPBnrsRQJ8m9Ivl1Qd2yoVGvuQVaH2cDad2QCdylq9Cj7Eyx1UuUBNYMteq6fGUAPS3KZCEIcXdfm2MTE6Frb2hvikAo0LSX5podIUAFV5yo86Y1dYRKZBOfLfgiVDK2SSBNQiFv3P65caXeKIiypRfZCOfavV0E6G0YU77vMfhs51XBtEIrSQZDZD

// The adaccounts edge is itself paginated (25 per page by default). Without
// following the cursor, entire ad accounts are silently dropped from every
// spend total in the app. Returns the same shape as a single axios response so
// existing callers keep working.
export const getMetaAdAccountsV23 = async () => {
  const allAccounts = [];
  let afterCursor = '';

  while (true) {
    const response = await axios.get(
      'https://graph.facebook.com/v23.0/me/adaccounts',
      {
        params: {
          fields: 'id,name',
          limit: 100,
          after: afterCursor,
          access_token:
            'EAAFkmxt66E8BPBnrsRQJ8m9Ivl1Qd2yoVGvuQVaH2cDad2QCdylq9Cj7Eyx1UuUBNYMteq6fGUAPS3KZCEIcXdfm2MTE6Frb2hvikAo0LSX5podIUAFV5yo86Y1dYRKZBOfLfgiVDK2SSBNQiFv3P65caXeKIiypRfZCOfavV0E6G0YU77vMfhs51XBtEIrSQZDZD'
        }
      }
    );

    allAccounts.push(...(response?.data?.data ?? []));

    const next = response?.data?.paging?.next;
    const nextCursor = response?.data?.paging?.cursors?.after;

    if (!next || !nextCursor) break;

    afterCursor = nextCursor;
  }

  return { data: { data: allAccounts } };
};

export const getAdAccountSpendForDateRange = (adAccountId, startDate, endDate) => {
  return axios.get(`https://graph.facebook.com/v23.0/${adAccountId}/insights`, {
    params: {
      access_token:
        'EAAFkmxt66E8BPBnrsRQJ8m9Ivl1Qd2yoVGvuQVaH2cDad2QCdylq9Cj7Eyx1UuUBNYMteq6fGUAPS3KZCEIcXdfm2MTE6Frb2hvikAo0LSX5podIUAFV5yo86Y1dYRKZBOfLfgiVDK2SSBNQiFv3P65caXeKIiypRfZCOfavV0E6G0YU77vMfhs51XBtEIrSQZDZD',
      fields: 'spend',
      level: 'account',
      'time_range[since]': startDate,
      'time_range[until]': endDate
    }
  });
};

export const getAdInsightsV23 = (
  adAccountId,
  afterCursor = '',
  timeRange = 'this_month',
  startDate = '',
  endDate = ''
) => {
  const params = {
    access_token:
      'EAAFkmxt66E8BPBnrsRQJ8m9Ivl1Qd2yoVGvuQVaH2cDad2QCdylq9Cj7Eyx1UuUBNYMteq6fGUAPS3KZCEIcXdfm2MTE6Frb2hvikAo0LSX5podIUAFV5yo86Y1dYRKZBOfLfgiVDK2SSBNQiFv3P65caXeKIiypRfZCOfavV0E6G0YU77vMfhs51XBtEIrSQZDZD',
    fields: 'campaign_name,adset_name,ad_name,spend,impressions,clicks,cpc,ctr',
    level: 'ad',
    limit: 50,
    after: afterCursor
  };

  if (timeRange !== 'custom') {
    params.date_preset = timeRange;
  }

  if (timeRange === 'custom') {
    params[`time_range[since]`] = startDate;
    params[`time_range[until]`] = endDate;
  }

  return axios.get(`https://graph.facebook.com/v23.0/${adAccountId}/insights`, {
    params: {
      ...params
    }
  });
};

const META_V23_TOKEN =
  'EAAFkmxt66E8BPBnrsRQJ8m9Ivl1Qd2yoVGvuQVaH2cDad2QCdylq9Cj7Eyx1UuUBNYMteq6fGUAPS3KZCEIcXdfm2MTE6Frb2hvikAo0LSX5podIUAFV5yo86Y1dYRKZBOfLfgiVDK2SSBNQiFv3P65caXeKIiypRfZCOfavV0E6G0YU77vMfhs51XBtEIrSQZDZD';

export const getMetaFacebookPages = (adAccountId) => {
  return axios.get(`https://graph.facebook.com/v23.0/${adAccountId}/promote_pages`, {
    params: {
      fields: 'id,name',
      access_token: META_V23_TOKEN
    }
  });
};

export const uploadMetaAdImage = (adAccountId, base64Image) => {
  return axios.post(
    `https://graph.facebook.com/v23.0/${adAccountId}/adimages`,
    {
      bytes: base64Image,
      access_token: META_V23_TOKEN
    }
  );
};

export const createMetaCampaign = (adAccountId, params) => {
  return axios.post(
    `https://graph.facebook.com/v23.0/${adAccountId}/campaigns`,
    {
      ...params,
      special_ad_categories: ['HOUSING'],
      access_token: META_V23_TOKEN
    }
  );
};

export const createMetaAdSet = (adAccountId, params) => {
  return axios.post(
    `https://graph.facebook.com/v23.0/${adAccountId}/adsets`,
    {
      ...params,
      access_token: META_V23_TOKEN
    }
  );
};

export const createMetaAdCreative = (adAccountId, params) => {
  return axios.post(
    `https://graph.facebook.com/v23.0/${adAccountId}/adcreatives`,
    {
      ...params,
      access_token: META_V23_TOKEN
    }
  );
};

export const createMetaAd = (adAccountId, params) => {
  return axios.post(
    `https://graph.facebook.com/v23.0/${adAccountId}/ads`,
    {
      ...params,
      access_token: META_V23_TOKEN
    }
  );
};
